// Zentrales State-Management – Singleton-Pattern (kein Redux, kein Framework)
// Alle Spieler-Daten werden hier gehalten und über Events propagiert.

// Typen aus @nighttrade/shared werden in Phase 0.5 voll befüllt.
// Für jetzt minimale Interfaces damit der Build funktioniert.

// Offline-Zusammenfassung – kommt beim ersten /auth/me Request nach Login (Phase 2.5)
export interface JobOfflineSummary {
  jobName: string;
  ticksProcessed: number;
  earnedCents: string;
  earnedXp: number;
  shiftCompleted: boolean;
}

export interface OfflineSummary {
  offlineHours: number;
  jobSummaries: JobOfflineSummary[];
  totalEarnedCents: string;      // BigInt als string
  totalEarnedXp: number;
  offlineCapHours: number;
  nextCapUnlock: string | null;
  // NT-PFAND-SCHICHT-01: Flaschensammeln mit dem Einkaufswagen bei offline beendeter Schicht
  pfandSchicht?: { collected: number; leftBehind: number } | null;
}

export interface PlayerSnapshot {
  id: string;
  username: string;
  level: number;
  xp: string;           // String wegen BigInt-Serialisierung
  restedXp: string;
  noxBalance: string;  // In Cent als String (z.B. "9900" = 99,00 €)
  blackMoney: string;
  wantedLevel: number;
  wantedPoints?: number;
  isInJail?: boolean;
  jailReleasesAt?: string | null;
  lastSeenAt: string;
  reputation?: number;            // Aus /auth/me
  prestigeRank?: number;          // Aus /auth/me
  prestigeTheme?: boolean;        // Aus /auth/me — rotes UI-Theme (NT-PRESTIGE-02, nur ab Prestige III wirksam)
  createdAt?: string;             // Aus /auth/me
  scheine?: number;               // Premium-Währung (Int, kein BigInt)
  politicalInfluence?: number;    // Politischer Einfluss (0–1000), Phase 2.6
  isAlphaTester?: boolean;        // Alpha-Tester-Badge
  hasFrueherVogelBadge?: boolean; // Früher-Vogel-Badge (NT-TITLES-01)
  crimeRankLabel?: string;        // Anzeige-Name des Crime-Ranks (z.B. "Normalbürger")
  crimeRankKey?: string;          // Maschinen-Key des Crime-Ranks (z.B. "NORMALBUERGER")
  crimeRankColorVar?: string | null; // Badge-Farbe (CSS-Var-Name) vom Server, null = kein Badge
  offlineSummary?: OfflineSummary | null; // Gesetzt beim ersten Login-Request
  accessStatus?: 'ACTIVE' | 'WAITLIST';   // NT-ALPHA-REWORK-01: WAITLIST → Warteliste-Seite, kein Spielzugang
}

interface GameStateData {
  player: PlayerSnapshot | null;
  sessionToken: string | null; // Nur für CSRF-Zwecke – echtes Token ist httpOnly Cookie
}

type StateEventType = 'player:updated' | 'auth:login' | 'auth:logout';
type StateListener = () => void;

class GameState {
  private data: GameStateData = {
    player: null,
    sessionToken: null,
  };

  private listeners = new Map<StateEventType, Set<StateListener>>();

  // ─── Auth ────────────────────────────────────────────────────────────────
  isAuthenticated(): boolean {
    return this.data.player !== null;
  }

  getPlayer(): PlayerSnapshot | null {
    return this.data.player;
  }

  // NT-ALPHA-REWORK-01: true, wenn der eingeloggte Spieler auf der Alpha-Warteliste steht.
  isWaitlisted(): boolean {
    return this.data.player?.accessStatus === 'WAITLIST';
  }

  setPlayer(player: PlayerSnapshot): void {
    this.data.player = player;
    this.emit('player:updated');
  }

  clearPlayer(): void {
    this.data.player = null;
    this.data.sessionToken = null;
    this.emit('auth:logout');
  }

  // CSRF-Token (nicht das Session-Token – das liegt als httpOnly Cookie)
  setCsrfToken(token: string): void {
    this.data.sessionToken = token;
  }

  getCsrfToken(): string | null {
    return this.data.sessionToken;
  }

  // ─── Events ──────────────────────────────────────────────────────────────
  on(event: StateEventType, listener: StateListener): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    const set = this.listeners.get(event);
    if (set) set.add(listener);
  }

  off(event: StateEventType, listener: StateListener): void {
    this.listeners.get(event)?.delete(listener);
  }

  private emit(event: StateEventType): void {
    this.listeners.get(event)?.forEach((listener) => { listener(); });
  }
}

// Singleton-Export – überall im Frontend dieselbe Instanz
export const gameState = new GameState();
